import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, Trash2, UserPlus, UserMinus, Crown, Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { formatDate } from '@/lib/formatDate'
import { track } from '@/lib/analytics'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import {
  listMembers,
  addTrackedPerson,
  renameTrackedPerson,
  removeTrackedPerson,
  removeUserMember,
  transferOwnership,
  updateMemberAccess,
  listPendingInvites,
  inviteUser,
  revokeInvite,
  resendInvite,
  type UserMember,
  type TrackedPerson,
  type PendingInvite,
  type HouseholdAccessRole,
} from '@/api/members'

function roleLabel(role: UserMember['role']) {
  if (role === 'OWNER') return 'Owner'
  if (role === 'VIEWER') return 'Viewer'
  return 'Member'
}

// --- Add Tracked Person Modal ---

function AddPersonModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => addTrackedPerson(name.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      onClose()
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 400) setError('Household has reached the 10 member limit.')
      else setError('Something went wrong. Please try again.')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-bold text-[var(--fl-text-1)] mb-4">Add Person</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fl-text-2)] mb-1">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder="e.g. Priya"
            />
          </div>
          {error && <p className="text-xs text-[var(--fl-error)]">{error}</p>}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending} className="flex-1">
              {mutation.isPending ? 'Adding…' : 'Add Person'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// --- Edit Tracked Person Modal ---

function EditPersonModal({ person, onClose }: { person: TrackedPerson; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(person.name)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => renameTrackedPerson(person.id, name.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      onClose()
    },
    onError: () => setError('Something went wrong. Please try again.'),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-bold text-[var(--fl-text-1)] mb-4">Edit Person</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fl-text-2)] mb-1">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
          </div>
          {error && <p className="text-xs text-[var(--fl-error)]">{error}</p>}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending} className="flex-1">
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// --- Delete Tracked Person Confirm ---

function DeletePersonConfirm({ person, onClose }: { person: TrackedPerson; onClose: () => void }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => removeTrackedPerson(person.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-bold text-[var(--fl-text-1)] mb-3">Remove {person.name}?</h2>
        <p className="text-sm text-[var(--fl-text-2)] mb-5">
          Transactions attributed to them will keep the attribution in history but {person.name}{' '}
          won't appear in new transaction dropdowns.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="flex-1"
          >
            {mutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// --- Remove User Member Confirm ---

function RemoveUserConfirm({ member, onClose }: { member: UserMember; onClose: () => void }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => removeUserMember(member.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      onClose()
    },
  })

  const name = member.displayName || member.email

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-bold text-[var(--fl-text-1)] mb-3">Remove {name}?</h2>
        <p className="text-sm text-[var(--fl-text-2)] mb-5">
          They will lose access immediately. Their transactions remain.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="flex-1"
          >
            {mutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// --- Transfer Ownership Confirm ---

function TransferOwnershipConfirm({
  member,
  onClose,
}: {
  member: UserMember
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => transferOwnership(member.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      onClose()
    },
  })

  const name = member.displayName || member.email

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-bold text-[var(--fl-text-1)] mb-3">Make {name} the owner?</h2>
        <p className="text-sm text-[var(--fl-text-2)] mb-5">
          {name} will become the household owner and you'll become a regular member. This can be
          undone later by having them transfer ownership back to you.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="flex-1"
          >
            {mutation.isPending ? 'Transferring…' : 'Transfer Ownership'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// --- Invite User Modal ---

function InviteUserModal({
  trackedPeople,
  onClose,
}: {
  trackedPeople: TrackedPerson[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [trackedPersonMemberId, setTrackedPersonMemberId] = useState('')
  const [role, setRole] = useState<HouseholdAccessRole>('MEMBER')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => inviteUser(email.trim(), trackedPersonMemberId || undefined, role),
    onSuccess: () => {
      track('member_invited', { role })
      queryClient.invalidateQueries({ queryKey: ['invites'] })
      // A pending invite ticks the onboarding checklist's "invite a family member" step.
      queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      onClose()
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 409)
        setError(
          'A pending invite already exists for this email, or this user is already a member.'
        )
      else if (status === 400) setError('Household has reached the 10 member limit.')
      else setError('Something went wrong. Please try again.')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email.trim()) {
      setError('Email is required.')
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-bold text-[var(--fl-text-1)] mb-4">Invite User</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fl-text-2)] mb-1">
              Email address
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--fl-text-2)] mb-1">
              Access level
            </label>
            <Select value={role} onChange={(v) => setRole(v as HouseholdAccessRole)}>
              <option value="MEMBER">Member — can add and edit household data</option>
              <option value="VIEWER">Viewer — can only view household data</option>
            </Select>
          </div>
          {trackedPeople.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-[var(--fl-text-2)] mb-1">
                Link to tracked person (optional)
              </label>
              <Select value={trackedPersonMemberId} onChange={setTrackedPersonMemberId}>
                <option value="">None</option>
                {trackedPeople.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-[var(--fl-text-2)] mt-1">
                If selected, this person's history will be linked to the invited user upon
                acceptance.
              </p>
            </div>
          )}
          {error && <p className="text-xs text-[var(--fl-error)]">{error}</p>}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending} className="flex-1">
              {mutation.isPending ? 'Sending…' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// --- Main page ---

export function MembersManager() {
  const { data: currentUser } = useCurrentUser()
  const { role: activeRole, isSample } = useCurrentHousehold()
  const queryClient = useQueryClient()

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['members'],
    queryFn: listMembers,
  })

  const { data: invitesData } = useQuery({
    queryKey: ['invites'],
    queryFn: listPendingInvites,
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites'] }),
  })

  const resendMutation = useMutation({
    mutationFn: (id: string) => resendInvite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] })
      toast.success('Invite email resent.')
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Failed to resend the invite. Please try again.')
    },
  })

  const accessMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: HouseholdAccessRole }) =>
      updateMemberAccess(id, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
  })

  const [showAddPerson, setShowAddPerson] = useState(false)
  const [showInviteUser, setShowInviteUser] = useState(false)
  const [editingPerson, setEditingPerson] = useState<TrackedPerson | null>(null)
  const [deletingPerson, setDeletingPerson] = useState<TrackedPerson | null>(null)
  const [removingUser, setRemovingUser] = useState<UserMember | null>(null)
  const [transferringTo, setTransferringTo] = useState<UserMember | null>(null)

  const users = membersData?.users ?? []
  const trackedPeople = membersData?.trackedPeople ?? []
  const pendingInvites = invitesData?.invites ?? []
  const totalActive = users.length + trackedPeople.length
  const atLimit = totalActive >= 10
  const isOwner = activeRole === 'OWNER'
  const isViewer = activeRole === 'VIEWER'

  if (membersLoading) {
    return <p className="text-sm text-[var(--fl-text-2)] p-4">Loading…</p>
  }

  return (
    <div className="space-y-8">
      {/* Users section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[var(--fl-text-2)] uppercase tracking-wider">
            Members
          </h2>
          {!isViewer && !isSample && (
            <Button
              variant="ghost"
              onClick={() => setShowInviteUser(true)}
              disabled={atLimit}
              title={atLimit ? 'Your household has reached the 10 member limit.' : undefined}
              className="h-auto gap-1.5 px-0 py-0 text-sm font-medium text-[var(--fl-primary)] hover:bg-transparent hover:text-[var(--fl-primary-hover)] disabled:cursor-not-allowed disabled:pointer-events-auto disabled:bg-transparent disabled:text-[var(--fl-text-2)] disabled:opacity-100"
            >
              <UserPlus size={14} />
              Invite User
            </Button>
          )}
        </div>

        <div className="bg-[var(--fl-surface)] rounded-xl border border-[var(--fl-border)] shadow-[rgba(0,0,0,0.05)_0px_1px_3px] divide-y divide-[var(--fl-surface-subtle)]">
          {users.map((member) => {
            const isMe = member.userId === currentUser?.id
            const name = member.displayName || member.email
            return (
              <div key={member.id} className="flex items-center gap-3 px-4 py-3">
                <Avatar avatarPreset={member.avatarPreset} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--fl-text-1)] truncate">{name}</p>
                  <p className="text-xs text-[var(--fl-text-2)] truncate">{member.email}</p>
                </div>
                <span
                  className={[
                    'text-xs font-medium px-2 py-0.5 rounded-full',
                    member.role === 'OWNER'
                      ? 'bg-[var(--fl-primary-faint)] text-[var(--fl-primary-hover)]'
                      : 'bg-[var(--fl-surface-hover)] text-[var(--fl-text-2)]',
                  ].join(' ')}
                >
                  {roleLabel(member.role)}
                </span>
                <p className="text-xs text-[var(--fl-text-2)] hidden sm:block whitespace-nowrap">
                  Joined {formatDate(member.joinedAt)}
                </p>
                {isOwner && !isMe && member.role !== 'OWNER' && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() =>
                      accessMutation.mutate({
                        id: member.id,
                        role: member.role === 'VIEWER' ? 'MEMBER' : 'VIEWER',
                      })
                    }
                    disabled={accessMutation.isPending}
                    tooltip={
                      member.role === 'VIEWER' ? 'Make full member' : 'Make read-only viewer'
                    }
                    className="ml-1 text-[var(--fl-text-2)] hover:text-[var(--fl-primary)]"
                  >
                    {member.role === 'VIEWER' ? <Eye size={14} /> : <EyeOff size={14} />}
                  </Button>
                )}
                {isOwner && !isMe && member.role !== 'OWNER' && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setTransferringTo(member)}
                    tooltip="Make owner"
                    className="ml-1 text-[var(--fl-text-2)] hover:text-[var(--fl-warning-text)]"
                  >
                    <Crown size={14} />
                  </Button>
                )}
                {isOwner && !isMe && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setRemovingUser(member)}
                    tooltip="Remove member"
                    className="ml-1 text-[var(--fl-text-2)] hover:text-red-500"
                  >
                    <UserMinus size={14} />
                  </Button>
                )}
              </div>
            )
          })}
        </div>

        {/* Pending invites */}
        {!isSample && pendingInvites.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-[var(--fl-text-2)] mb-2 uppercase tracking-wider">
              Pending Invites
            </p>
            <div className="bg-[var(--fl-surface)] rounded-xl border border-[var(--fl-border)] shadow-[rgba(0,0,0,0.05)_0px_1px_3px] divide-y divide-[var(--fl-surface-subtle)]">
              {pendingInvites.map((invite: PendingInvite) => (
                <div key={invite.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--fl-text-1)] truncate">
                      {invite.invitedEmail}
                    </p>
                    <p className="text-xs text-[var(--fl-text-2)]">
                      Expires {formatDate(invite.expiresAt)}
                    </p>
                  </div>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--fl-surface-hover)] text-[var(--fl-text-2)]">
                    {invite.role === 'VIEWER' ? 'Viewer' : 'Member'}
                  </span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--fl-warning-bg)] text-[var(--fl-warning-text)]">
                    Pending
                  </span>
                  {isOwner && (
                    <Button
                      variant="ghost"
                      onClick={() => resendMutation.mutate(invite.id)}
                      disabled={resendMutation.isPending}
                      className="ml-1 h-auto px-0 py-0 text-xs text-[var(--fl-text-2)] hover:bg-transparent hover:text-[var(--fl-primary)]"
                    >
                      Resend
                    </Button>
                  )}
                  {isOwner && (
                    <Button
                      variant="ghost"
                      onClick={() => revokeMutation.mutate(invite.id)}
                      className="ml-1 h-auto px-0 py-0 text-xs text-[var(--fl-text-2)] hover:bg-transparent hover:text-red-500"
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Tracked People section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[var(--fl-text-2)] uppercase tracking-wider">
            Tracked People
          </h2>
          {!isViewer && (
            <Button
              variant="ghost"
              onClick={() => setShowAddPerson(true)}
              disabled={atLimit}
              title={atLimit ? 'Your household has reached the 10 member limit.' : undefined}
              className="h-auto gap-1.5 px-0 py-0 text-sm font-medium text-[var(--fl-primary)] hover:bg-transparent hover:text-[var(--fl-primary-hover)] disabled:cursor-not-allowed disabled:pointer-events-auto disabled:bg-transparent disabled:text-[var(--fl-text-2)] disabled:opacity-100"
            >
              <UserPlus size={14} />
              Add Person
            </Button>
          )}
        </div>

        {trackedPeople.length === 0 ? (
          <div className="bg-[var(--fl-surface)] rounded-xl border border-[var(--fl-border)] shadow-[rgba(0,0,0,0.05)_0px_1px_3px] px-4 py-8 text-center">
            <p className="text-sm text-[var(--fl-text-2)]">No tracked people yet.</p>
            <p className="text-xs text-[var(--fl-text-2)] mt-1">
              Add family members without login access to attribute transactions to them.
            </p>
          </div>
        ) : (
          <div className="bg-[var(--fl-surface)] rounded-xl border border-[var(--fl-border)] shadow-[rgba(0,0,0,0.05)_0px_1px_3px] divide-y divide-[var(--fl-surface-subtle)]">
            {trackedPeople.map((person) => (
              <div key={person.id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-full bg-[var(--fl-surface-hover)] flex items-center justify-center text-[var(--fl-primary)] text-sm font-semibold shrink-0">
                  {person.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--fl-text-1)] truncate">
                    {person.name}
                  </p>
                  <p className="text-xs text-[var(--fl-text-2)]">
                    Added {formatDate(person.joinedAt)}
                  </p>
                </div>
                {!isViewer && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setEditingPerson(person)}
                      className="text-[var(--fl-text-2)] hover:text-[var(--fl-primary)]"
                      tooltip="Edit"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setDeletingPerson(person)}
                      className="text-[var(--fl-text-2)] hover:text-red-500"
                      tooltip="Remove"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Modals */}
      {showAddPerson && <AddPersonModal onClose={() => setShowAddPerson(false)} />}
      {showInviteUser && (
        <InviteUserModal trackedPeople={trackedPeople} onClose={() => setShowInviteUser(false)} />
      )}
      {editingPerson && (
        <EditPersonModal person={editingPerson} onClose={() => setEditingPerson(null)} />
      )}
      {deletingPerson && (
        <DeletePersonConfirm person={deletingPerson} onClose={() => setDeletingPerson(null)} />
      )}
      {removingUser && (
        <RemoveUserConfirm member={removingUser} onClose={() => setRemovingUser(null)} />
      )}
      {transferringTo && (
        <TransferOwnershipConfirm member={transferringTo} onClose={() => setTransferringTo(null)} />
      )}
    </div>
  )
}
